import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Shield, ShieldCheck, Eye, Pencil, Ban, RotateCcw } from 'lucide-react';

const API = '';

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'bg-red-100 text-red-700', icon: Shield, desc: 'Full system access' },
  manager: { label: 'Manager', color: 'bg-blue-100 text-blue-700', icon: ShieldCheck, desc: 'Operational access' },
  supervisor: { label: 'Supervisor', color: 'bg-amber-100 text-amber-700', icon: Eye, desc: 'Create only' },
  data_entry: { label: 'Data Entry', color: 'bg-purple-100 text-purple-700', icon: Eye, desc: 'Production data entry only' },
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'supervisor' });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/users`, { withCredentials: true });
      setUsers(data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updates = { name: form.name, role: form.role };
        if (form.password) updates.password = form.password;
        await axios.put(`${API}/api/users/${editingUser.id}`, updates, { withCredentials: true });
        toast.success('User updated');
      } else {
        await axios.post(`${API}/api/users`, form, { withCredentials: true });
        toast.success('User created');
      }
      setDialogOpen(false);
      setEditingUser(null);
      setForm({ email: '', password: '', name: '', role: 'supervisor' });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operation failed');
    }
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ email: u.email, password: '', name: u.name, role: u.role });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditingUser(null);
    setForm({ email: '', password: '', name: '', role: 'supervisor' });
    setDialogOpen(true);
  };

  const toggleActive = async (u) => {
    try {
      if (u.active) {
        await axios.delete(`${API}/api/users/${u.id}`, { withCredentials: true });
        toast.success('User deactivated');
      } else {
        await axios.put(`${API}/api/users/${u.id}`, { active: true }, { withCredentials: true });
        toast.success('User reactivated');
      }
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  return (
    <div data-testid="user-management-page" className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">User Management</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage users and their role-based permissions</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" onClick={openNew} className="bg-zinc-900 hover:bg-zinc-800">
              <UserPlus className="w-4 h-4 mr-2" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              {!editingUser && (
                <Input data-testid="user-email-input" type="email" placeholder="Email" required
                  value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              )}
              <Input data-testid="user-name-input" placeholder="Full Name" required
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              <Input data-testid="user-password-input" type="password"
                placeholder={editingUser ? 'New password (leave empty to keep)' : 'Password'} 
                required={!editingUser}
                value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
              <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — Full access</SelectItem>
                  <SelectItem value="manager">Manager — Operational</SelectItem>
                  <SelectItem value="supervisor">Supervisor — Create only</SelectItem>
                  <SelectItem value="data_entry">Data Entry — Production data entry</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid="user-submit-btn" type="submit" className="w-full bg-zinc-900">
                {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Role Legend */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {Object.entries(ROLE_CONFIG).map(([key, cfg]) => (
          <div key={key} className="bg-white border border-zinc-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <cfg.icon className="w-4 h-4 text-zinc-600" />
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">{cfg.desc}</p>
          </div>
        ))}
      </div>

      {/* Users Table */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-zinc-600">Name</th>
              <th className="text-left px-4 py-3 font-medium text-zinc-600">Email</th>
              <th className="text-left px-4 py-3 font-medium text-zinc-600">Role</th>
              <th className="text-left px-4 py-3 font-medium text-zinc-600">Status</th>
              <th className="text-right px-4 py-3 font-medium text-zinc-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="text-center py-8 text-zinc-400">Loading...</td></tr>
            ) : users.map(u => {
              const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.supervisor;
              return (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 font-medium text-zinc-900">{u.name}</td>
                  <td className="px-4 py-3 text-zinc-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${rc.color}`}>{rc.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    {u.active !== false ? (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
                    ) : (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-500">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button data-testid={`edit-user-${u.email}`} variant="ghost" size="sm" onClick={() => openEdit(u)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`toggle-user-${u.email}`} variant="ghost" size="sm" onClick={() => toggleActive(u)}>
                      {u.active !== false ? <Ban className="w-3.5 h-3.5 text-red-500" /> : <RotateCcw className="w-3.5 h-3.5 text-emerald-500" />}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
